import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  getPaymentTemplatesByProjectId,
  createPaymentTemplates
} from '../../services/api/implementation/impl';
import { useQuery, useMutation } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import AddPaymentTemplate from './AddPaymentTemplate';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { numberToWords } from '../../utils/numberToWords';

// Validation schema
const validationSchema = yup.object().shape({
  absolute: yup.number().required('Final Price is required'),
  gst_percent: yup.string().required('GST % is required'),
  lead_id: yup.number().required('Lead is required'),
  unit_id: yup.number().required('Unit is required'),
  paymentType: yup.string().required('Payment Type is required'),
  template_id: yup
    .number()
    .nullable()
    .when('paymentType', {
      is: 'Time Based',
      then: (schema) => schema.required('Payment Template is required'),
      otherwise: (schema) => schema.nullable()
    })
});

export const CloseDeal = ({ onClose, onSubmit, isSubmitting, unitData, selectedLead }) => {
  const userId = Cookies.get('userId');
  const [priceInWords, setPriceInWords] = useState('');
  const [isOpenTemplateModal, setIsOpenTemplateModal] = useState(false);
  const [isSubmittingTemplate, setIsSubmittingTemplate] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      lead_id: selectedLead?.id,
      unit_id: unitData?.id,
      absolute: '',
      gst_percent: '',
      paymentType: '',
      template_id: null
    }
  });

  const paymentType = watch('paymentType');

  // Fetch payment templates for the selected project
  const projectID = unitData?.project?.id;
  const { data: paymentTemplates, refetch: refetchPaymentTemplates } = useQuery({
    queryKey: ['payment_templates', projectID],
    queryFn: () => getPaymentTemplatesByProjectId(projectID),
    enabled: !!projectID,
    onError: (error) => {
      console.error('Error fetching payment templates:', error);
    }
  });

  // Mutation for creating payment template
  const { mutate: handleCreateTemplate } = useMutation({
    mutationFn: (payload) => createPaymentTemplates(payload),
    onMutate: () => {
      setIsSubmittingTemplate(true);
    },
    onSuccess: () => {
      setIsSubmittingTemplate(false);
      toast.success('Payment template created successfully');
      setIsOpenTemplateModal(false);
      refetchPaymentTemplates();
    },
    onError: (error) => {
      setIsSubmittingTemplate(false);
      console.error('Error creating payment template:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error creating payment template'
      );
    }
  });

  // Transform payment templates for dropdown
  const paymentTemplateOptions =
    paymentTemplates?.map((template) => ({
      label: template.templateName,
      id: template.id,
      value: template.id
    })) || [];

  // Watch for changes in the absolute (Final Price) field and convert to words
  const finalPrice = watch('absolute');
  useEffect(() => {
    if (finalPrice && Number(finalPrice) > 0) {
      setPriceInWords(numberToWords(Number(finalPrice)));
    } else {
      setPriceInWords('');
    }
  }, [finalPrice]);

  const onSubmitForm = (data) => {
    const { absolute, gst_percent, ...rest } = data;
    const payload = {
      ...rest,
      lead_id: parseInt(data.lead_id),
      unit_id: parseInt(data.unit_id),
      total_price: parseFloat(absolute),
      gst_percentage: parseInt(gst_percent),
      paymentType: data.paymentType
    };

    // Only include template_id if payment type is "Time Based"
    if (data.paymentType === 'Time Based' && data.template_id) {
      payload.template_id = Number(data.template_id);
    }

    onSubmit(payload);
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Close Deal</span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="grid grid-cols-2 gap-4">
          {/* Project Name */}
          <FormInput
            width={100}
            label={'Project Name'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'project_name'}
            valuePre={unitData?.project?.name || ''}
          />

          {/* Block Name */}
          <FormInput
            width={100}
            label={'Block Name'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'block_name'}
            valuePre={unitData?.block?.name || ''}
          />

          {/* Level */}
          <FormInput
            width={100}
            label={'Level'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'level'}
            valuePre={unitData?.level?.name || ''}
          />

          {/* Unit */}
          <FormInput
            width={100}
            label={'Unit'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'unit'}
            valuePre={unitData?.unitName || ''}
          />

          {/* Unit Price */}
          <FormInput
            width={100}
            label={'Unit Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'unit_price'}
            valuePre={unitData?.cost ? `₹${parseInt(unitData.cost).toLocaleString('en-IN')}` : ''}
          />

          <FormInput
            width={100}
            label={'GST'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'gst'}
            valuePre={unitData?.gst || ''}
          />

          {unitData?.extra_charges !== '0' && (
            <FormInput
              width={100}
              label={'Extra Charges'}
              type={'text'}
              readOnly={true}
              register={register}
              register_key={'extra_charges'}
              valuePre={unitData?.extra_charges || ''}
            />
          )}

          <FormInput
            width={100}
            label={'Plc Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'plc_price'}
            valuePre={unitData?.plc_price || ''}
          />

          <FormInput
            width={100}
            label={'Total Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'cost'}
            valuePre={`₹${(
              (parseInt(unitData?.cost) || 0) +
              (parseInt(unitData?.plc_price) || 0) +
              (parseInt(unitData?.gst) || 0)
            ).toLocaleString('en-IN')}.00`}
          />

          {/* GST % Radio Buttons */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium">GST %</label>
            <div className="flex gap-4">
              <label>
                <input type="radio" value="0" {...register('gst_percent')} />
                0%
              </label>
              <label>
                <input type="radio" value="1" {...register('gst_percent')} />
                1%
              </label>
              <label>
                <input type="radio" value="5" {...register('gst_percent')} />
                5%
              </label>
            </div>
            {errors.gst_percent && (
              <span className="text-red-500 text-xs">{errors.gst_percent.message}</span>
            )}
          </div>

          {/* Payment Type */}
          <FormInput
            width={100}
            label={'Payment Type'}
            type={'dropdown'}
            register={register}
            register_key={'paymentType'}
            errors={errors.paymentType}
            options={[
              { label: 'Select Payment Type', value: '', disabled: true, selected: true },
              { label: 'Time Based', value: 'Time Based' },
              { label: 'Construction Based', value: 'Construction Based' }
            ]}
            allvalues={(value) => {
              // Reset template_id when payment type changes
              setValue('template_id', null);
            }}
          />

          {/* Payment Template - only show if Time Based is selected */}
          {paymentType === 'Time Based' && (
            <div className="flex relative">
              <FormInput
                width={100}
                label={'Payment Template'}
                type={'dropdown'}
                register={register}
                register_key={'template_id'}
                errors={errors.template_id}
                options={[
                  { label: 'Select Template', value: '', disabled: true, selected: true },
                  ...paymentTemplateOptions
                ]}
              />
              <div className="absolute right-0 top-0 ">
                <button
                  type="button"
                  className="text-sm text-blue-500 underline"
                  onClick={() => {
                    if (!projectID) {
                      toast.error('Project ID is required to create a template');
                      return;
                    }
                    setIsOpenTemplateModal(true);
                  }}>
                  Add New
                </button>
              </div>
            </div>
          )}

          {/* Discount (absolute) - moved to last */}
          <div className="flex flex-col">
            <FormInput
              width={100}
              label={'Final Price'}
              register={register}
              register_key={'absolute'}
              type={'number'}
              errors={errors.absolute}
              onType={(value) => {
                const numValue = Number(value);
                if (numValue && numValue > 0) {
                  setPriceInWords(numberToWords(numValue));
                } else {
                  setPriceInWords('');
                }
              }}
            />
            {priceInWords && (
              <span className="text-sm text-gray-600 mt-1 italic">{priceInWords}</span>
            )}
          </div>

          {/* Hidden fields */}
          <input type="hidden" {...register('lead_id')} />
          <input type="hidden" {...register('unit_id')} />
        </div>

        <div className="flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            Close Deal
          </Button>
        </div>
      </form>

      {/* Add Payment Template Modal */}
      {isOpenTemplateModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Create Payment Template'}
          width={55}
          onClose={() => {
            setIsOpenTemplateModal(false);
          }}>
          <AddPaymentTemplate
            isSubmitting={isSubmittingTemplate}
            projectId={projectID}
            handleOnSubmit={handleCreateTemplate}
            onClose={() => {
              setIsOpenTemplateModal(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};
